/*
  Shared favorites engine for Hey Jack.

  Favorited product handles live in localStorage under hj_favorites as a JSON
  array, oldest first. The module is storage-agnostic at its surface: every read
  flows through has/list/count and every write through add/remove/toggle, so the
  backing store can later swap to a customer-account API without touching a
  single caller. All localStorage access is wrapped in try/catch because private
  browsing can throw on read or write.

  On every write the module dispatches 'hjfavorites:change' on document, with the
  changed handle and its new state in event.detail. Listeners (the header count
  badge, hearts elsewhere on the page, the favorites page) react to that event
  rather than polling.

  Hearts are wired by delegation: bindHearts attaches one document-level click
  listener that toggles whichever [data-favorite-toggle] was clicked, so hearts
  injected later (recently viewed, recommendations, the favorites page) work with
  no extra wiring. A MutationObserver gives those injected hearts their correct
  filled/outline visual the moment they enter the DOM.

  init(root) drives the dedicated favorites page: it reads list(), fetches each
  product fresh from the Shopify product JSON endpoint, and renders cards whose
  markup mirrors snippets/product-card.liquid (the same approach as
  recently-viewed.js), then shows or hides the empty state.

  renderCarousel(root, options) drives the homepage and product-page favorites
  carousels: a capped, newest-first strip of the same cards, revealed only when
  there is something favorited. It uses the same fetch-and-build approach as
  init, but inserts cards into the shared carousel track and re-renders on every
  'hjfavorites:change', so favoriting or unfavoriting anywhere (including a
  card's own filled heart on the strip itself) keeps the strip current and hides
  it again once it empties. init() is for the dedicated /pages/favorites page
  (all favorites, its own empty state); renderCarousel() is the surface variant.

  Exposes window.HeyJackFavorites.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'hj_favorites';
  var CHANGE_EVENT = 'hjfavorites:change';
  var CAROUSEL_MAX = 10;

  // ----- storage (the only place that touches localStorage) -----

  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return [];
      }
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {}
  }

  // Persist the new list, then announce the change so every listener (header
  // badge, the other hearts for this handle, the favorites page) can react.
  // detail carries the one handle that changed and whether it is now favorited.
  function commit(handles, handle, favorited) {
    writeHandles(handles);
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
      detail: { handle: handle, favorited: favorited, handles: handles.slice() }
    }));
  }

  // ----- public, storage-agnostic API -----

  function list() {
    return readHandles();
  }

  function count() {
    return readHandles().length;
  }

  function has(handle) {
    return readHandles().indexOf(handle) !== -1;
  }

  // Append a handle if missing. Idempotent: a second add is a no-op (and emits
  // no change event), so callers never need to check first.
  function add(handle) {
    if (!handle) {
      return;
    }
    var handles = readHandles();
    if (handles.indexOf(handle) !== -1) {
      return;
    }
    handles.push(handle);
    commit(handles, handle, true);
  }

  function remove(handle) {
    if (!handle) {
      return;
    }
    var handles = readHandles();
    var i = handles.indexOf(handle);
    if (i === -1) {
      return;
    }
    handles.splice(i, 1);
    commit(handles, handle, false);
  }

  // Flip a handle's state and return the new state (true = now favorited).
  function toggle(handle) {
    if (!handle) {
      return false;
    }
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  // ----- rendering helpers -----

  var HEART_PATH = 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z';

  // Local money formatter. The product JSON endpoint returns prices in cents.
  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2);
  }

  // Request a sized variant of a Shopify CDN image by adding a width param.
  function sizeImage(url, width) {
    if (!url) {
      return '';
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ----- heart visuals -----

  // A favorited heart is filled (fill=currentColor); an unfavorited one is an
  // outline (fill=none). The button also carries .is-favorited so existing CSS
  // (the card heart's terracotta tint) applies, and aria-pressed mirrors state.
  function paintHeart(btn, favorited) {
    if (!btn) {
      return;
    }
    btn.classList.toggle('is-favorited', !!favorited);
    btn.setAttribute('aria-pressed', favorited ? 'true' : 'false');
    var svg = btn.querySelector('svg');
    if (svg) {
      svg.setAttribute('fill', favorited ? 'currentColor' : 'none');
    }
  }

  // Set the correct visual on every heart inside root (default: whole document)
  // from current storage.
  function syncHearts(root) {
    var scope = root || document;
    var hearts = scope.querySelectorAll('[data-favorite-toggle]');
    for (var i = 0; i < hearts.length; i++) {
      paintHeart(hearts[i], has(hearts[i].getAttribute('data-handle')));
    }
  }

  // ----- heart wiring (delegation + auto-sync of injected hearts) -----

  var heartsBound = false;

  function bindHearts(root) {
    // Initial visuals for the hearts already present at bind time.
    syncHearts(root || document);

    if (heartsBound) {
      return;
    }
    heartsBound = true;

    // One document-level click listener handles every [data-favorite-toggle],
    // including hearts injected after load. preventDefault stops the card's
    // wrapping <a> from navigating when the heart inside it is clicked.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-favorite-toggle]') : null;
      if (!btn) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-handle');
      if (!handle) {
        return;
      }
      paintHeart(btn, toggle(handle));
    });

    // Keep every heart for a handle in sync whenever its state changes anywhere
    // (a product can appear in more than one place at once).
    document.addEventListener(CHANGE_EVENT, function (e) {
      var changed = e.detail && e.detail.handle;
      var hearts = document.querySelectorAll('[data-favorite-toggle]');
      for (var i = 0; i < hearts.length; i++) {
        var handle = hearts[i].getAttribute('data-handle');
        if (changed && handle !== changed) {
          continue;
        }
        paintHeart(hearts[i], has(handle));
      }
    });

    // Give hearts injected after load (recently viewed, recommendations, the
    // favorites page) their correct visual the moment they enter the DOM, so no
    // injection site needs to know about this module.
    if (typeof window.MutationObserver === 'function' && document.body) {
      var observer = new window.MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var a = 0; a < added.length; a++) {
            var node = added[a];
            if (node.nodeType !== 1) {
              continue;
            }
            if (node.matches && node.matches('[data-favorite-toggle]')) {
              paintHeart(node, has(node.getAttribute('data-handle')));
            }
            if (node.querySelectorAll) {
              var inner = node.querySelectorAll('[data-favorite-toggle]');
              for (var i = 0; i < inner.length; i++) {
                paintHeart(inner[i], has(inner[i].getAttribute('data-handle')));
              }
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ----- favorites page card (mirrors snippets/product-card.liquid) -----

  // Build one card element from a product JSON object. Same structure as the
  // Liquid card and as recently-viewed.js, except the heart is FILLED and wired
  // to toggle (every card here is, by definition, favorited). data-fav-card
  // carries the handle so the page can drop the card when it is unfavorited.
  function buildCard(product) {
    var handle = product.handle || '';
    var title = product.title || '';
    var price = product.price;
    var compareAt = product.compare_at_price;
    var onSale = compareAt && compareAt > price;
    var featured = product.featured_image;
    var images = product.images || [];
    var second = images.length > 1 ? images[1] : null;

    var html = '';
    html += '<a class="hj-pcard" href="/products/' + escapeHtml(handle) + '" data-fav-card="' + escapeHtml(handle) + '">';
    html += '<div class="hj-pcard__panel">';
    html += '<div class="hj-pcard__badges">';
    if (onSale) {
      html += '<span class="hj-badge hj-badge--sale">On Sale</span>';
    }
    html += '</div>';
    html += '<button class="hj-pcard__fav is-favorited" type="button" aria-label="Remove from favorites" aria-pressed="true" data-favorite-toggle data-handle="' + escapeHtml(handle) + '">';
    html += '<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="' + HEART_PATH + '"></path></svg>';
    html += '</button>';
    if (featured) {
      html += '<img class="hj-pcard__img hj-pcard__img--base" src="' + escapeHtml(sizeImage(featured, 600)) + '" alt="' + escapeHtml(title) + '" loading="lazy" />';
      if (second) {
        html += '<img class="hj-pcard__img hj-pcard__img--hover" src="' + escapeHtml(sizeImage(second, 600)) + '" alt="' + escapeHtml(title) + '" loading="lazy" />';
      }
    } else {
      html += '<div class="hj-pcard__art"></div>';
    }
    html += '</div>';
    html += '<div class="hj-pcard__info">';
    html += '<h3 class="hj-pcard__title">' + escapeHtml(title) + '</h3>';
    html += '<p class="hj-pcard__price">';
    if (onSale) {
      html += '<span class="hj-pcard__price-was">' + formatMoney(compareAt) + '</span>';
      html += '<span class="hj-pcard__price-now">' + formatMoney(price) + '</span>';
    } else {
      html += '<span class="hj-pcard__price-now">' + formatMoney(price) + '</span>';
    }
    html += '</p>';
    html += '</div>';
    html += '</a>';

    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.firstElementChild;
  }

  // ----- favorites page -----

  // Render the favorites page inside root. Reads list(), fetches each product
  // fresh (so prices and images never go stale), and fills [data-favorites-list]
  // with cards in favorited order. Shows [data-favorites-empty] only when there
  // is nothing to show. After the first render it listens for changes, so
  // unfavoriting a card (from its own heart or anywhere else) drops it and
  // re-checks the empty state.
  function init(root) {
    if (!root) {
      return;
    }
    var listEl = root.querySelector('[data-favorites-list]');
    var emptyEl = root.querySelector('[data-favorites-empty]');
    if (!listEl) {
      return;
    }

    function setEmptyState() {
      var hasCards = listEl.querySelector('[data-fav-card]') !== null;
      if (emptyEl) {
        emptyEl.hidden = hasCards;
      }
    }

    // Drop any rendered card whose handle is no longer favorited, then re-check
    // the empty state. Driven by the change event, so it reflects an unfavorite
    // from this page or from anywhere else.
    function reconcile() {
      var current = list();
      var cards = listEl.querySelectorAll('[data-fav-card]');
      for (var i = 0; i < cards.length; i++) {
        if (current.indexOf(cards[i].getAttribute('data-fav-card')) === -1) {
          cards[i].parentNode.removeChild(cards[i]);
        }
      }
      setEmptyState();
    }

    document.addEventListener(CHANGE_EVENT, reconcile);

    var handles = list();
    if (handles.length === 0) {
      setEmptyState();
      return;
    }

    var fetches = handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .catch(function () {
          return null;
        });
    });

    Promise.all(fetches).then(function (products) {
      products.forEach(function (product) {
        if (!product || !product.handle) {
          return;
        }
        listEl.appendChild(buildCard(product));
      });
      setEmptyState();
    });
  }

  // ----- homepage / product-page carousel -----

  // Fill the favorites carousel inside root and reveal it, then keep it current.
  // root holds a [data-favorites-list] marker sitting directly in the shared
  // carousel track (snippets/carousel.liquid). Like recently-viewed.js, cards
  // are inserted as siblings before that marker so each becomes a track item and
  // picks up the carousel sizing; the marker itself is hidden so it never shows
  // as an empty phantom column, but it stays in the DOM as the insertion point
  // for every re-render. options.excludeHandle keeps the current product (on a
  // product page) out of its own strip; the homepage passes none.
  //
  // render() runs once now and again on every 'hjfavorites:change', so the strip
  // tracks favoriting and unfavoriting from anywhere, including a card's own
  // filled heart here (the unfavorited card drops out on the next render). When
  // nothing is left to show, the whole section is hidden again.
  function renderCarousel(root, options) {
    if (!root) {
      return;
    }
    options = options || {};
    var excludeHandle = options.excludeHandle || '';

    var listEl = root.querySelector('[data-favorites-list]');
    if (!listEl) {
      return;
    }
    var track = listEl.parentNode;
    if (!track) {
      return;
    }
    // The marker is only an insertion anchor, never a visible column.
    listEl.style.display = 'none';

    function clearCards() {
      var cards = track.querySelectorAll('[data-fav-card]');
      for (var i = 0; i < cards.length; i++) {
        cards[i].parentNode.removeChild(cards[i]);
      }
    }

    function render() {
      clearCards();

      // Favorites are stored oldest first; show newest first, drop the excluded
      // handle, and cap the strip.
      var handles = list().slice().reverse().filter(function (h) {
        return h && h !== excludeHandle;
      }).slice(0, CAROUSEL_MAX);

      if (handles.length === 0) {
        root.style.display = 'none';
        return;
      }

      var fetches = handles.map(function (handle) {
        return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      });

      Promise.all(fetches).then(function (products) {
        // Clear again in case a change fired (and re-rendered) mid-fetch, so the
        // freshest render wins instead of stacking on a stale one.
        clearCards();
        var shown = 0;
        products.forEach(function (product) {
          if (!product || !product.handle) {
            return;
          }
          track.insertBefore(buildCard(product), listEl);
          shown += 1;
        });
        root.style.display = shown > 0 ? '' : 'none';
      });
    }

    render();
    document.addEventListener(CHANGE_EVENT, render);
  }

  // ----- header count badge -----

  // Mirror the cart badge: show the count over the icon, hide it at zero.
  function refreshBadge() {
    var badge = document.querySelector('.hj-header__favorites-count');
    if (!badge) {
      return;
    }
    var n = count();
    badge.textContent = String(n);
    badge.hidden = n === 0;
  }

  // ----- expose + self-init -----

  window.HeyJackFavorites = {
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    list: list,
    count: count,
    init: init,
    renderCarousel: renderCarousel,
    bindHearts: bindHearts,
    syncHearts: syncHearts
  };

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  // Document-level delegation means the module self-inits: no init script in the
  // layout is needed. Wire the hearts, paint the header badge, and keep the badge
  // in sync with every later change.
  ready(function () {
    bindHearts(document);
    refreshBadge();
    document.addEventListener(CHANGE_EVENT, refreshBadge);
  });
})();
